import { flexRender, Table } from '@tanstack/react-table';
import { ComponentPropsWithoutRef, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import { TableBody, TableCell, TableHead, TableHeader, Table as TablePrimitive, TableRow } from '../table/table';

type BaseTableRow = {
  id: string;
};

type TableNewProps<TData extends BaseTableRow> = {
  table: Table<TData>;
  onSelectRow?: (id: string) => void;
  noResultsMessage?: string;
} & Omit<ComponentPropsWithoutRef<'table'>, 'children'>;

/**
 * The main table component that renders headers, rows, and handles selection.
 * Extends all native HTML table attributes for maximum flexibility.
 *
 * @template TData - The type of data being displayed. Must have an `id` field.
 *
 * @param props.table - TanStack Table instance created with useReactTable
 * @param props.onSelectRow - Callback for single-select row clicks
 * @param props.noResultsMessage - Custom message when table is empty
 * @param props - All native HTML table attributes (className, aria-*, data-*, etc.)
 *
 * ## Column Sizing
 * - Uses `table-layout: fixed` for predictable, consistent column widths
 * - Column widths calculated from `size` property in column definitions
 * - Widths are proportional: `size: 30` vs `size: 10` creates a 3:1 ratio
 * - Applied as percentage widths for responsive behavior
 *
 * ## Row Selection
 * - **Single-select**: Pass `onSelectRow` callback. Visual state via `meta.selectedRowId`
 * - **Multi-select**: Pass `meta.selectedRowIds` and `meta.setSelectedRowIds`. Clicking row toggles selection.
 *   Checkboxes in cells are automatically detected and won't trigger row toggle.
 *
 * ## Mobile Responsiveness
 * - Tables automatically scroll horizontally on screens below 1024px (lg breakpoint)
 * - Minimum table width of 800px ensures proper column layout
 * - Scroll container is keyboard accessible and announced to screen readers
 * - No additional wrapper needed - this is handled internally
 *
 * ## Accessibility
 * - **Native ARIA support**: All ARIA attributes available (aria-label, aria-labelledby, aria-rowcount, etc.)
 * - **Table context**: Use `aria-label` or `aria-labelledby` for screen reader context
 * - **Scrollable region**: Horizontal scroll container has `role="region"` and is keyboard accessible
 * - **Row indices**: Each row has `aria-rowindex` for large dataset navigation
 * - **Selection announcements**: Live region announces selection changes to screen readers
 * - **Keyboard navigation**: Interactive rows are focusable with Tab key; scroll container focusable with Tab
 * - **Row selection**: Press Enter or Space to select focused row
 * - **Focus indicator**: Yellow ring appears around focused row/scroll container (keyboard only)
 * - **Screen readers**: Rows announce selection state via `aria-selected`, scroll container announces "Scrollable table content"
 * - **Semantic HTML**: Proper table structure with `role="row"` attributes
 *
 * ## Scrollable Tables
 * - Wrap the table in `VerticalScrollArea` when you need vertical scrolling
 *
 * @example Single-select
 * ```tsx
 * const table = useReactTable({
 *   data,
 *   columns,
 *   getCoreRowModel: getCoreRowModel(),
 *   meta: { selectedRowId: '123' }
 * });
 *
 * <DataTableNew.Table
 *   table={table}
 *   onSelectRow={(id) => console.log('Selected:', id)}
 * />
 * ```
 *
 * @example Multi-select
 * ```tsx
 * const [selectedIds, setSelectedIds] = useState<string[]>([]);
 *
 * const table = useReactTable({
 *   data,
 *   columns,
 *   getCoreRowModel: getCoreRowModel(),
 *   meta: {
 *     selectedRowIds: selectedIds,
 *     setSelectedRowIds: setSelectedIds,
 *   }
 * });
 *
 * <DataTableNew.Table table={table} />
 * ```
 *
 * @example Accessibility with aria-label
 * ```tsx
 * <DataTableNew.Table
 *   table={table}
 *   aria-label="Recent transactions table showing 50 entries"
 * />
 * ```
 *
 * @example Accessibility with aria-labelledby
 * ```tsx
 * <h2 id="transactions-heading">Recent Transactions</h2>
 * <DataTableNew.Table
 *   table={table}
 *   aria-labelledby="transactions-heading"
 * />
 * ```
 *
 * @example Large dataset with row count
 * ```tsx
 * <DataTableNew.Table
 *   table={table}
 *   aria-label="Transactions table"
 *   aria-rowcount={1000}
 * />
 * ```
 *
 * @example Scrollable table
 * ```tsx
 * <VerticalScrollArea className="h-96">
 *   <DataTableNew.Table table={table} />
 * </VerticalScrollArea>
 * ```
 */
export const TableNew = <TData extends BaseTableRow>({
  table,
  onSelectRow,
  noResultsMessage = 'No results',
  className,
  ...tableProps
}: TableNewProps<TData>) => {
  const meta = table.options.meta;
  const columns = table.getAllColumns().length;
  const totalSize = table.getTotalSize();

  // Live region for selection announcements
  const liveRegionRef = useRef<HTMLDivElement>(null);

  // Announce selection changes to screen readers
  useEffect(() => {
    if (!liveRegionRef.current) return;

    const selectedCount = meta?.selectedRowIds?.length ?? 0;
    const totalRows = table.getRowModel().rows.length;

    if (selectedCount > 0) {
      liveRegionRef.current.textContent = `${selectedCount} of ${totalRows} rows selected`;
    } else {
      liveRegionRef.current.textContent = '';
    }
  }, [meta?.selectedRowIds, table]);

  return (
    <>
      {/* Live region for screen reader announcements */}
      <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="sr-only" />

      {/* Scrollable region for responsive horizontal scrolling */}
      <div
        role="region"
        aria-label="Scrollable table content"
        tabIndex={0}
        className="overflow-x-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset lg:overflow-x-visible"
      >
        <TablePrimitive {...tableProps} className={cn('w-full min-w-[800px] table-fixed caption-bottom', className)}>
          <TableHeader className="text-xs text-muted-foreground">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const columnWidth = (header.column.getSize() / totalSize) * 100;

                  return (
                    <TableHead
                      key={header.id}
                      className={header.column.getIndex() === 0 ? 'pl-5' : undefined}
                      style={{ width: `${columnWidth}%` }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="text-sm text-black">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, index) => {
                const isSelected =
                  meta?.selectedRowId === row.original.id || meta?.selectedRowIds?.includes(row.original.id);
                const isEvenRow = index % 2 === 0;
                const isInteractive = !!(onSelectRow || meta?.setSelectedRowIds);

                // Handler for row selection (used by both click and keyboard)
                const handleRowSelect = () => {
                  // Multi-select logic
                  if (meta?.setSelectedRowIds && meta?.selectedRowIds) {
                    const id = row.original.id;
                    const selected = meta.selectedRowIds;

                    if (selected.includes(id)) {
                      meta.setSelectedRowIds(selected.filter((sid) => sid !== id));
                    } else {
                      meta.setSelectedRowIds([...selected, id]);
                    }
                    return;
                  }

                  // Single-select logic
                  onSelectRow?.(row.original.id);
                };

                return (
                  <TableRow
                    key={row.id}
                    role="row"
                    tabIndex={isInteractive ? 0 : undefined}
                    aria-selected={isInteractive ? isSelected : undefined}
                    aria-rowindex={index + 2} // +2 because aria-rowindex is 1-based and row 1 is header
                    data-state={row.getIsSelected() && 'selected'}
                    className={cn('cursor-pointer px-5', !isEvenRow ? 'bg-muted/50' : '', {
                      'cursor-auto': !isInteractive,
                      'hover:bg-muted/50': !isInteractive && !isEvenRow,
                      'hover:bg-transparent': !isInteractive && isEvenRow,
                      // Keyboard focus styles
                      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset':
                        isInteractive,
                    })}
                    style={meta?.rowHeight ? { height: `${meta.rowHeight}px` } : undefined}
                    onClick={(e) => {
                      if (!isInteractive) return;

                      // Prevent toggling if the click originated from a checkbox
                      if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;

                      handleRowSelect();
                    }}
                    onKeyDown={(e) => {
                      if (!isInteractive) return;

                      // Handle Enter or Space key
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); // Prevent page scroll on Space
                        handleRowSelect();
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            meta?.rowHeight ? 'overflow-hidden' : '',
                            cell.column.getIndex() === 0 ? 'pl-5' : undefined,
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns} className="h-24 text-center">
                  <p className="text-sm text-muted-foreground">{noResultsMessage}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TablePrimitive>
      </div>
    </>
  );
};
